/**
 * The lock's real rate: Encompass field 2160, the buy-side net buy rate.
 *
 * Encompass 2160 = 2152 (`rate_lock_buy_side_rate`, the buy-side BASE rate)
 *                + 2159 (the sum of buy-side lines whose
 *                  `priceAdjustmentType === 'BaseRate'`).
 * We derive it from the synced adjustment lines. On 2026-10-12 the derivation
 * matched Encompass's own 2160 on 942/942 loans; LW26082725 carried a +0.375
 * "Other" rate adjustment that left 2152 at 8.125 while the lock was 8.500.
 *
 * Arithmetic contract — mirrored exactly by SQL
 * `public._lock_material_net_buy_rate` (identity key
 * `rate_lock_buy_side_net_buy_rate`):
 * - JSON numbers use their binary64 shortest decimal (`String(n)`, exponent
 *   form expanded); JSON strings stay exact after removing `, $ %` and
 *   whitespace. Anything else is non-numeric and ignored.
 * - Each value rounds half away from zero to integer micro-units (6 decimals)
 *   by decimal-digit arithmetic, never by float multiplication.
 * - Domain: |base| + Σ|line| < 1,000,000 after rounding. Outside it, or for a
 *   non-finite number, nothing is derived.
 *
 * `lockRateValue` returns the raw base untouched unless a nonzero rate
 * adjustment exists, so every consumer keeps its existing coercion, formatting
 * and fallbacks for unadjusted loans.
 *
 * Import-free on purpose: edge functions, Vitest and the portal bundle (via
 * src/services/lockNetBuyRate.ts) all import this file.
 */

export interface LockRateFields {
  rate_lock_buy_side_rate?: unknown;
  rate_lock_buy_side_adjustments?: unknown;
}

const MICRO_PER_UNIT = 1_000_000;
const DOMAIN_LIMIT_MICRO = 1_000_000 * MICRO_PER_UNIT;
const DECIMAL_TEXT = /^-?([0-9]+(\.[0-9]+)?|\.[0-9]+)$/;
const EXPONENT_TEXT = /^(-?)(\d+)(?:\.(\d+))?e([+-]\d+)$/;

type RateMicro =
  | { kind: 'micro'; micro: number }
  | { kind: 'nonnumeric' }
  | { kind: 'out_of_domain' };

interface LockRateAnalysis {
  baseMicro: number;
  adjustmentMicro: number;
}

// String(n) switches to exponent form below 1e-6 and from 1e21; expand it to
// plain digits so rounding reads the same decimal text SQL reads.
function plainDecimalText(text: string): string {
  const match = EXPONENT_TEXT.exec(text);
  if (!match) return text;
  const [, sign, intPart, fracPart = '', exponentText] = match;
  const digits = intPart + fracPart;
  const pointIndex = intPart.length + Number(exponentText);
  if (pointIndex <= 0) return `${sign}0.${'0'.repeat(-pointIndex)}${digits}`;
  if (pointIndex >= digits.length) return `${sign}${digits}${'0'.repeat(pointIndex - digits.length)}`;
  return `${sign}${digits.slice(0, pointIndex)}.${digits.slice(pointIndex)}`;
}

function microFromDecimalText(text: string): RateMicro {
  const negative = text.startsWith('-');
  const [intPart, fracPart = ''] = (negative ? text.slice(1) : text).split('.');
  const intDigits = intPart.replace(/^0+/, '');
  if (intDigits.length > 6) return { kind: 'out_of_domain' };
  const fraction = `${fracPart}0000000`.slice(0, 7);
  let micro = Number(intDigits || '0') * MICRO_PER_UNIT + Number(fraction.slice(0, 6));
  if (Number(fraction[6]) >= 5) micro += 1;
  if (micro === 0) return { kind: 'micro', micro: 0 };
  return { kind: 'micro', micro: negative ? -micro : micro };
}

function rateMicro(value: unknown): RateMicro {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return { kind: 'out_of_domain' };
    return microFromDecimalText(plainDecimalText(String(value)));
  }
  if (typeof value === 'string') {
    const cleaned = value.replace(/[,$%\s]/g, '');
    return DECIMAL_TEXT.test(cleaned) ? microFromDecimalText(cleaned) : { kind: 'nonnumeric' };
  }
  return { kind: 'nonnumeric' };
}

function isBaseRateLine(line: unknown): line is { rate?: unknown } {
  return typeof line === 'object'
    && line !== null
    && (line as { priceAdjustmentType?: unknown }).priceAdjustmentType === 'BaseRate';
}

function analyzeLockRate(fields: LockRateFields | null | undefined): LockRateAnalysis | null {
  if (!fields) return null;
  const base = rateMicro(fields.rate_lock_buy_side_rate);
  if (base.kind !== 'micro') return null;
  let magnitudeMicro = Math.abs(base.micro);
  if (magnitudeMicro >= DOMAIN_LIMIT_MICRO) return null;
  let adjustmentMicro = 0;
  const lines = fields.rate_lock_buy_side_adjustments;
  if (Array.isArray(lines)) {
    for (const line of lines) {
      if (!isBaseRateLine(line)) continue;
      const parsed = rateMicro(line.rate);
      if (parsed.kind === 'nonnumeric') continue;
      if (parsed.kind === 'out_of_domain') return null;
      magnitudeMicro += Math.abs(parsed.micro);
      if (magnitudeMicro >= DOMAIN_LIMIT_MICRO) return null;
      adjustmentMicro += parsed.micro;
    }
  }
  return { baseMicro: base.micro, adjustmentMicro };
}

function microToRate(micro: number): number {
  return micro / MICRO_PER_UNIT;
}

/** Sum of the BaseRate lines in micro-units; null when nothing can be derived. */
export function lockRateAdjustmentMicro(fields: LockRateFields | null | undefined): number | null {
  return analyzeLockRate(fields)?.adjustmentMicro ?? null;
}

/** Encompass 2160 as a number; null when the base is non-numeric or out of domain. */
export function lockNetBuyRate(fields: LockRateFields | null | undefined): number | null {
  const analysis = analyzeLockRate(fields);
  return analysis ? microToRate(analysis.baseMicro + analysis.adjustmentMicro) : null;
}

/**
 * The value every consumer shows or compares as "the lock's rate": the raw
 * `rate_lock_buy_side_rate`, untouched, unless a nonzero rate adjustment
 * exists, in which case the net buy rate.
 */
export function lockRateValue(fields: LockRateFields | null | undefined): unknown {
  const raw = fields?.rate_lock_buy_side_rate;
  const analysis = analyzeLockRate(fields);
  if (!analysis || analysis.adjustmentMicro === 0) return raw;
  return microToRate(analysis.baseMicro + analysis.adjustmentMicro);
}

/**
 * Value of the conditional identity key `rate_lock_buy_side_net_buy_rate`:
 * present only when a nonzero rate adjustment exists.
 */
export function materialNetBuyRate(fields: LockRateFields | null | undefined): number | null {
  const analysis = analyzeLockRate(fields);
  if (!analysis || analysis.adjustmentMicro === 0) return null;
  return microToRate(analysis.baseMicro + analysis.adjustmentMicro);
}
